import { useFieldArray, useForm, useWatch } from "react-hook-form";
import {
  DatePicker,
  Input,
  Label,
  MultipleSelect,
  Select,
  Slider,
  Submit,
  Switch,
  TextArea,
} from "../FormComponents";
import useLabels from "../../hooks/useLabels";

// NEW: Import CacheManager for surgical cache updates
import CacheManager from "../../services/cacheManager";
import { add, updateOp, setOperations } from "../../context/operations/operationsSlice";
import {
  setWarehouse,
} from "../../context/warehouse/warehouseSlice";
import { useNavigate, useParams } from "react-router";
import useAxiosPrivate from "../../hooks/useAxiosPrivate";
import { useDispatch, useSelector } from "react-redux";
import {
  getOperations,
  newOperation,
  updateOperation,
  getWarehouseByType,
  getPomacesList,
} from "../../services/utils";
import { buildPomace, ConfirmationModal, CTA, DestinationSelector, FilterIcon, OperationRecap, OptionalNoteRow, PomacesDestination, ProtocolConflictModal, SourceSelector, TankIcon } from "../Generic";
import {
  ALPHA_NUM_SPACE_DASH_UNDERSCORE_REGEX,
  INT_FLOAT_REGEX,
  INT_REGEX,
  NUM_ALPHA_SPACE_APEX_DOT_ALL_LEN_REGEX,
} from "../../constants/regex";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { toast } from "react-toastify";
import { selectAllLotNames, selectOperations, selectWarehouseByType } from "../../context/selectors";
import { addMonths } from "date-fns";
import { DestTankDetail } from "../OperationsDetail/Details";
import { NavLink } from "react-router-dom";
import { emptyTanks } from "../../context/tanks/tanksSlice";
import { emptyLots } from "../../context/lots/lotsSlice";
import { toastAxios } from "../../services/toastAxios";
import {
  getInputTankUnit,
  inputToLiters,
  litersToInput,
} from "../../services/tankUnit";

const TransferForm = forwardRef(({
  operation,
  lots,
  activeWinery,
  tanks,
  users,
  configs,
  type,
  isWineMakingApplication = false,
  isDuplicate = false,
  isMobile,
  onConfigSave,
  forcedSrcTank,
}, formRef) => {

  const { id } = useParams();
  const [getLabel] = useLabels();
  const inputUnit = getInputTankUnit();
  const navigate = useNavigate();
  const axiosPrivate = useAxiosPrivate();
  const dispatch = useDispatch();
  const lotNames = useSelector(selectAllLotNames);
  const [isOpen, setIsOpen] = useState(false);
  const [isConflictOpen, setIsConflictOpen] = useState(false);
  const [conflictProtocols, setConflictProtocols] = useState([]);
  const [pendingPayload, setPendingPayload] = useState(null);
  const [isExactDate, setIsExactDate] = useState(false);
  const [isPressed, setIsPressed] = useState([]);
  const [lotTypeMap, setLotTypeMap] = useState(() => {
    // Wizard back-navigation: pre-populate from saved dest_tanks batch_type
    if (!onConfigSave || !operation?.dest_tanks?.length) {
      return {};
    }
    const map = {};
    operation.dest_tanks.forEach((d) => {
      if (d.new_batch_name != null) {
        map[d.new_batch_name] = d.batch_type ?? 0;
      }
    });
    return map;
  });

  const [pomacesRecipients, setPomacesRecipients] = useState([]);
  const filters = useSelector((state) => selectWarehouseByType("filters")(state));

  // Store previous type so we only reset when filter_type changes
  const prevFilterType = useRef(null);

  const wineMakingData =
    type === "DECANT"
      ? JSON.parse(localStorage.getItem("wineMakingData")) || {}
      : {};
  const closeData =
    type === "CLOSING"
      ? JSON.parse(localStorage.getItem("closeTank")) || null
      : null;
  const operations = useSelector(selectOperations);
  // const lotNames = useSelector(selectAllLotNames);

  const {
    watch,
    register,
    handleSubmit,
    formState: { errors, isValid },
    setError,
    clearErrors,
    reset,
    setValue,
    getValues,
    control,
    trigger,
  } = useForm({
    defaultValues: {
      dest_tanks: operation?.dest_tanks || [
        { tankData: { value: null, label: null }, quantity: 0 },
      ],
      housingFields: operation?.housing_filters || [
        { filterData: { value: null, label: null, name: null }},
      ],
      priority: operation?.priority || "",
      expire_date: operation?.expire_date || null,
      type: { type },
      tank_id_in: operation?.tank_id_in || "",
      cellarman_ids: operation?.cellarman_ids || "",
      new_batch_name: operation?.new_batch_name || "",
    },
    mode: "onTouched",
  });

  const { dest_tanks, housing_filters, tank_id_in, filter_type, pomaces, subtype } = useWatch({ control });

  useImperativeHandle(formRef, () => ({
    submitForm: async () => {
      const isFormValid = await trigger();
      if (!isFormValid) return;
      handleSubmit(() => {
        if (onConfigSave) {
          sendData();
        } else {
          openFormModal();
        }
      })();
    },
  }), [handleSubmit, onConfigSave, trigger]);

  const { fields, append, remove, replace, update } =
    useFieldArray({
      control,
      name: "dest_tanks",
    });

  const { fields: housingFields, append: housingAppend, remove: housingRemove, replace: housingReplace } =
    useFieldArray({
      control,
      name: "housing_filters",
    });

  const loadOperations = async () => {
    const loadedOperations = await getOperations(axiosPrivate);
    dispatch(setOperations(loadedOperations));
  };

  const loadWarehouse = async () => {
    const currentData = await getWarehouseByType(axiosPrivate, "filters");
    dispatch(setWarehouse({ type: "filters", data: currentData }));
  };

  useEffect(() => {
    // localStorage.removeItem('tanks');

    if (type === "FILTERING" && (!filters || filters?.length === 0)) {
      loadWarehouse();
    } else if (type === "CLOSING" && !id && closeData) {
      const tank =
        tanks?.find((t) => t.id === Number(closeData)) ||
        null;
      if (!tank) return;
      setValue("tank_id_in", {
        value: tank?.id,
        label: tank?.name,
        color: tank?.color,
        state: tank?.state,
        quantity: tank?.quantity,
        capacity: tank?.capacity,
        color: tank?.color,
        lot: tank?.batch_name,
        planned_quantity: tank?.planned_quantity,
        is_planned: tank?.is_planned === true,
          });

      localStorage.removeItem('closeTank');
      return;
    }
    if (!id && type === "RACKING") {
      setValue("pomaces.reason", {
        label: getLabel("pomaceReason_POMACE"),
        value: "POMACE",
      });
    }

    // Wizard restore: operation prop contains previously-saved config
    if (!id && operation && onConfigSave) {
      const dests = [];
      const tmpLotTypeMap = {};
      const tmpIsPressed = [];

      operation.dest_tanks?.forEach((d) => {
        const tank = tanks?.find((t) => t.id === d.tank_id);

        // Calculate expected destination state based on operation type (for validation highlighting)
        const expectedDestState =
          type === "DESTEMMING"
            ? operation.operation_params?.subtype === "CARBONIC_MACERATION"
              ? "WINE"
              : "MUST"
            : ["RACKING", "FILTERING"].includes(type)
            ? "WINE"
            : type === "BOTTLING"
            ? "BOTTLED"
            : type === "CLOSING"
            ? "EMPTY"
            : tank?.state || "WINE";

        dests.push({
          quantity: d.quantity,
          tankData: {
            value: tank?.id ?? d.tank_id,
            label: tank?.name ?? `#${d.tank_id}`,
            type: tank?.type,
            state: tank?.state || expectedDestState,
            barrels_num: tank?.barrels_num,
            capacity: Number(tank?.capacity),
            quantity: Number(tank?.quantity),
            availability: Number(tank?.capacity) - Number(tank?.quantity),
            color: tank?.color,
            lot: lots?.find((lot) => lot?.id === tank?.batch_id)?.name || getLabel("tankState_EMPTY"),
            batch_name: d.new_batch_name,
            ...((tank?.state || expectedDestState) === "WINE" && {
              batch_type: tank?.batch_type === 1 ? "FLOWER" : "PRESSED",
            }),
            programmed_as_src: tank?.programmed_as_src,
            programmed_as_dest: tank?.programmed_as_dest,
          },
          batch_name_selector: {
            value: d.is_new,
            label: d.is_new ? getLabel("lotNewName") : d.new_batch_name,
          },
          new_batch_name: d.is_new ? d.new_batch_name : "",
        });
        if (d.new_batch_name != null) {
          tmpLotTypeMap[d.new_batch_name] = d.batch_type ?? 0;
        }
        tmpIsPressed.push(d.batch_type ?? 0);
      });

      if (dests.length > 0) {
        replace(dests);
        setLotTypeMap(tmpLotTypeMap);
        setIsPressed(tmpIsPressed);
      } else {
        // console.log('[TransferForm] Wizard restore: dests is empty! operation.dest_tanks:', operation.dest_tanks, 'tanks:', tanks);
      }

      // Restore RACKING pomaces
      if (type === "RACKING" && operation.pomaces?.length > 0) {
        const tmpPomaces = operation.pomaces[0] || {};
        setValue("pomaces", {
          quantity: litersToInput(tmpPomaces?.quantity, inputUnit),
          cost: tmpPomaces?.cost,
          close_notes: tmpPomaces?.notes || "",
        });
        setValue("pomaces.dest", { value: tmpPomaces?.recipient_id, label: tmpPomaces?.recipient });
        setValue("pomaces.reason", { value: tmpPomaces?.waste_type, label: getLabel(`pomaceReason_${tmpPomaces?.waste_type}`) });
      }
      return;
    }

    if (!id && wineMakingData?.dest_tanks?.length > 0) {
      const tank =
        tanks?.find((t) => t.id === wineMakingData?.dest_tanks?.[0]?.tank_id) ||
        null;
      if (!tank) return;
      setValue("tank_id_in", {
        value: tank?.id,
        label: tank?.name,
        color: tank?.color,
        state: tank?.state,
        quantity: tank?.quantity,
        capacity: tank?.capacity,
        color: tank?.color,
        lot: tank?.batch_name,
        planned_quantity: tank?.planned_quantity,
        is_planned: tank?.is_planned === true,
      });

      setValue("priority", {
        label: getLabel(`operationPriority${wineMakingData?.priority}`),
        value: wineMakingData?.priority,
      });

      const cellarmans = [];
      wineMakingData?.cellarman_ids?.map((u) => {
        const user = users?.find((us) => us.id === u);
        cellarmans.push({
          value: u,
          label: user?.username,
        });
      });
      setValue("cellarman_ids", cellarmans);

      setValue("expire_date", wineMakingData?.expire_date);

      // if (wineMakingData?.expire_date || cellarmans?.length === 0 || tank === null)
      // history.pushState();
      return;
    } else if (!id && wineMakingData?.dest_tanks?.length === 0) {
      localStorage.removeItem("wineMakingData");
      // navigate('/programming/winemaking/new');
      return;
    }

    if (id && (!operations || operations?.length === 0)) loadOperations();
    if (id && operations?.length > 0) {
      const operation = operations?.find((o) => o.id === Number(id)) || null;
      if (operation === null) return;

      const tank = tanks?.find(
        (t) => t.id === operation?.src_tanks[0]?.tank_id
      );
      setValue("tank_id_in", {
        value: tank?.id,
        label: tank?.name,
        color: tank?.color,
        type: tank?.type,
        state: tank?.state,
        barrels_num: tank?.barrels_num,
        quantity: tank?.quantity,
        capacity: tank?.capacity,
        color: tank?.color,
        lot: tank?.batch_name,
        planned_quantity: tank?.is_planned ? Number(tank?.planned_quantity) : tank?.quantity,
        is_planned: tank?.is_planned === true,
        is_planned_by_other_ops: tank?.is_planned_src && tank?.programmed_as_src?.filter(p_id => id === null || p_id !== Number(id))?.length > 0,
        programmed_as_src: tank?.programmed_as_src,
        programmed_as_dest: tank?.programmed_as_dest,
      
      });

      setValue("priority", {
        label: getLabel(`operationPriority${operation?.priority}`),
        value: operation?.priority,
      });

      const tmpIsPressed = [];
      const dests = [];

      const tmpLotTypeMap = {};

      operation?.dest_tanks?.map((s, i) => {
        const tank = tanks?.find((t) => t.id === s.tank_id);
        const name = tank?.is_planned === true ? tank?.planned_batch_name : tank?.batch_name;
        let is_new = tank?.is_planned && tank?.is_planned_state ? true : s?.is_new;
        if (is_new) {
          is_new = (lots
              ?.find(
                (l) => l?.name === name 
                && l?.status !== "PLANNED" 
                && l?.state === tank?.planned_batch_state 
                && l?.name?.toLowerCase?.()
              ) === undefined);
          
          if (is_new) {
            // NOW detect “duplicate new” earlier in this same op
            const alreadyUsed = operation?.dest_tanks
              .slice(0, i)   // only look at earlier entries
              .some(prev => {
                // whichever field actually holds the “new” name in your data
                const prevName = prev?.is_planned === true 
                  ? prev?.planned_batch_name 
                  : prev?.batch_name;
                return prev?.is_new && prevName === name;
              });
      
            if (alreadyUsed) {
              is_new = false;
            }
          }
        }

        dests.push({
          quantity: litersToInput(s?.arrow_quantity, inputUnit),
          tankData: {
            value: tank?.id,
            label: tank?.name,
            type: tank?.type,
            state: tank?.state || s?.state,
            barrels_num: tank?.barrels_num,
            capacity: Number(tank?.capacity),
            quantity: Number(tank?.quantity),
            availability: Number(tank?.capacity) - Number(tank?.quantity),
            color: tank?.color,
            lot:
              lots?.find((lot) => lot?.id === tank?.batch_id)?.name ||
              getLabel("tankState_EMPTY"),
            ...((tank?.state || s?.state) === "WINE" && {
              batch_type: tank?.batch_type === 1 ? "FLOWER" : "PRESSED",    // TO DO check
            }),
            batch_name: is_new ? s?.batch_name : lots?.find((lot) => lot?.id === tank?.batch_id)?.name,
            is_planned_by_other_ops: tank?.is_planned && tank?.programmed_as_dest?.filter(p_id => id === null || p_id !== Number(id))?.length > 0,
            programmed_as_src: tank?.programmed_as_src,
            programmed_as_dest: tank?.programmed_as_dest,
          },
          batch_name_selector: {
            value: is_new,
            label: is_new ? getLabel("lotNewName") : s?.new_batch_name || s?.batch_name,
          },
          new_batch_name: is_new ? s?.batch_name : "",
          ...(type === "CLOSING" && {
              close_dest: s?.close_dest && s?.close_id ? { value: s?.close_id, label: s?.close_dest } : { label: '', value: '' },
              close_reason: s?.close_reason ? { value: s?.close_reason, label: getLabel(`buyReason_${s?.close_reason}`) } : { label: '', value: '' },
              close_notes: s?.close_notes || '',
            }),
        });
        
        // 2 = MIXED, 1 = FLOWER, 0 = PRESSED → se è FLOWER, metti false → quindi sarà batch_type: 1
        // Se è PRESSED, metti true → batch_type: 0
        tmpLotTypeMap[(tank?.batch_name || tank?.planned_batch_name)] = s?.batch_type;
        tmpIsPressed.push(s?.batch_type);
      });
        
      replace(dests);

      setLotTypeMap(tmpLotTypeMap);

      setIsPressed(tmpIsPressed);

      const cellarmans = [];
      operation?.cellarman_ids?.map((u) => {
        const user = users?.find((us) => us.id === u);
        cellarmans.push({
          value: u,
          label: user?.username,
        });
      });

      // const pomaces = [];
      //   operation?.pomaces?.map((s) => {
      //     const tank = tanks?.find((t) => t.id === s.tank_id);
      //     pomaces.push({
      //       quantity: s?.quantity * 100 / chosenQuantity,
      //       tankData: {
      //         value: tank?.id,
      //         label: tank?.name,
      //         capacity: Number(tank?.capacity),
      //         quantity: Number(tank?.quantity),
      //         availability:
      //         Number(tank?.capacity) - Number(tank?.quantity),
      //         color: tank?.color,
      //         lot:
      //         lots.find((lot) => lot?.id === tank?.batch_id)?.name ||
      //         getLabel("tankState_EMPTY"),
      //       },
      //     });
      //   });
      // replaceP(operation?.pomaces);

      if (operation?.type === "RACKING") {
        const tmpPomaces = operation?.pomaces?.[0] || {};
        const pomaces = {
          quantity: litersToInput(tmpPomaces?.quantity, inputUnit),
          cost: tmpPomaces?.cost,
          close_notes: tmpPomaces?.notes || "",
        }
        setValue("pomaces", pomaces);
        setValue("pomaces.dest", { value: tmpPomaces?.id, label: tmpPomaces?.recipient });
        setValue("pomaces.reason", { value: tmpPomaces?.waste_type, label: getLabel(`pomaceReason_${tmpPomaces?.waste_type}`) });
      }    
      // setValue("pomaces_quantity", operation?.pomaces);
      setValue("cellarman_ids", cellarmans);

      setValue("expire_date", operation?.expire_date);

      setValue("note", operation?.note);

      // if (lots?.find((l) => l.name === operation?.batch_name_out)) {
      //   setValue("batch_name_selector", {
      //     value: false,
      //     label: operation?.batch_name_out,
      //   });
      //   setValue("new_batch_name", "");
      // } else {
      //   setValue("batch_name_selector", {
      //     value: true,
      //     label: getLabel("lotNewName"),
      //   });
      // }
    }

    // Only run in edit mode (!onConfigSave), not in wizard mode
    if (!id && !onConfigSave) {
      // Populate lotTypeMap from existing lots in edit mode
      const tmpLotTypeMap = {};
      for (const l of lotNames) {
        if (["WINE", "BOTTLED"].includes(l?.state)) {
          tmpLotTypeMap[l?.name] = l?.batch_type; // 0 = PRESSED
        }
      }
      setLotTypeMap(tmpLotTypeMap);
    }
  }, [operations, operation, lots]);
  
  useEffect(() => {
    if (!["RACKING", "FILTERING"].includes(type)) return;

    const fetchPomaces = async () => {
      const pomaces = await getPomacesList(axiosPrivate);
      setPomacesRecipients(pomaces);
    };
    fetchPomaces();


    const newType = filter_type?.value;
    const oldType = prevFilterType.current;

    // Only reset when filter_type actually CHANGED
    if (newType !== oldType) {
      prevFilterType.current = newType;

      if (["CARTON", "TANGENTIAL"].includes(newType)) {
        // Reset simple filter
        setValue("filter", { value: null, label: null, type: null }, { shouldDirty: false });
        // Reset housing filters to a single empty row
        setValue(
          "housing_filters",
          [{ filterData: { value: null, label: null, name: null }}],
          { shouldDirty: false }
        );
      }

      if (newType === "HOUSING") {
        setValue("filter", { value: null, label: null, type: null }, { shouldDirty: false });
        setValue(
          "housing_filters",
          [{filterData: { value: null, label: null, name: null }}],
          { shouldDirty: false }
        );
      }
    }
  }, [filter_type, type, setValue]);

  // Pre-fill and lock source tank in multi-move wizard mode
  useEffect(() => {
    if (!forcedSrcTank) return;
    setValue("tank_id_in", {
      value: forcedSrcTank.id,
      label: forcedSrcTank.name,
      color: forcedSrcTank.color,
      type: forcedSrcTank.type,
      state: forcedSrcTank.state,
      barrels_num: forcedSrcTank.barrels_num,
      quantity: forcedSrcTank.quantity,
      capacity: forcedSrcTank.capacity,
      lot: forcedSrcTank.batch_name,
      lot_id: forcedSrcTank.batch_id,
      planned_quantity: forcedSrcTank.quantity,
      is_planned: false,
      is_planned_by_other_ops: false,
      programmed_as_src: [],
      programmed_as_dest: [],
    });
  }, [forcedSrcTank, setValue]);

  const customIdNewOp = "custom-id-new-op";
  const customIdUpdateOp = "custom-id-update-op";
  const sendData = async () => {
    const data = getValues();
    // pomaces.quantity è in unità input: convertiamo a litri per il BE.
    // (dest_tanks vengono convertiti esplicitamente più sotto.)
    if (data?.pomaces) {
      data.pomaces = {
        ...data.pomaces,
        quantity: inputToLiters(data.pomaces?.quantity, inputUnit),
      };
    }
    const dests = [];
    try {
      data?.dest_tanks
        ?.filter(
          (d) =>
            (type === "CLOSING" ? d?.close_dest?.value : d?.tankData?.value) &&
            d?.quantity
            // &&
            // ((d?.batch_name_selector?.value === true && d?.new_batch_name) ||
            //   (d?.batch_name_selector?.value === false &&
            //     d?.batch_name_selector?.label))
        )
        ?.forEach((destT, index) => {
          const tankData = destT?.tankData;
          
          const isPlanned =
            tankData?.value &&
            tankData?.is_planned_by_other_ops &&
            Number(tankData?.quantity) === 0 &&
            tankData?.is_planned;
    
          const new_batch_name = isPlanned
            ? tankData?.planned_batch_name
            : destT?.new_batch_name;
    
          const batch_name_label = destT?.batch_name_selector?.label;
          const batch_name_value = destT?.batch_name_selector?.value;
    
          const new_name = (
            batch_name_value || isPlanned ? new_batch_name : batch_name_label
          )?.trim();

          const isPlannedByOtherOps = tankData?.is_planned && (
            (id === null && tankData?.programmed_as_dest?.length > 0) ||
            (id !== null && tankData?.programmed_as_dest?.some(p_id => p_id !== Number(id))));
          
          const destLot =
            lots?.find((l) => l?.name === new_name && l.state === "MUST") || null;
          const bType = destLot?.id && !isPlannedByOtherOps ? "OLD" : "NEW";

          const t = tanks?.find((t) => t.id === destT?.tankData?.value);
          
          const destQuantityLiters = inputToLiters(destT.quantity, inputUnit);
          const dest = {
            tank_id: destT?.tankData?.value,
            quantity: destQuantityLiters,
            is_new:
              batch_name_value === true && new_batch_name?.length > 0,
            new_batch_name: (batch_name_value === true
              ? new_batch_name
              : batch_name_label
            )?.trim() || null,
            new_batch_type: bType,
            old_batch_id:
              t?.batch_id ||
              "",
            old_quantity:
              t?.quantity ||
              0,
            new_quantity:
              (Number(
                t?.quantity ||
                  0
              ) || 0) + (destQuantityLiters || 0),
            ...(type === "RACKING" && {
              // batch_type: read from form field (_batch_type) if available, else fallback to lotTypeMap
              batch_type: destT?._batch_type !== undefined ? destT._batch_type : (lotTypeMap?.[new_batch_name] ?? 0),
            }),
            ...(type === "CLOSING" && {
              // batch_type: 
              //   (!isPressed?.[index] ? "FLOWER" : "PRESSED") || "PRESSED",
              close_dest_id: destT?.close_dest?.is_new === true ? null : destT?.close_dest?.value,
              close_dest_name: destT?.close_dest?.is_new === false ? null : destT?.close_dest?.label,
              close_dest_is_new: destT?.close_dest?.is_new === true,
              close_reason: destT?.close_reason?.value,
              close_notes: destT?.close_notes,
            }),
          };

          if (dest.new_batch_name === null) {
            dest.new_batch_name = t?.planned_batch_name || '';
          }
          dests.push(dest);
        });
    } catch (err) {
      toast.error(getLabel("toast_invalidDestTanks"), {
        toastId: customIdInvalidLotName,
        position: toast.POSITION.BOTTOM_RIGHT,
        exclude: true,
        autoClose: 5000,
      });
      return;
    }

    if (dests?.length === 0 || data?.dest_tanks?.length !== dests?.length) {
      toast.error(
        getLabel(
          dests?.length === 0
            ? "toast_missingDestTanks"
            : "toast_invalidDestTanks"
        ),
        {
          toastId: customIdInvalidLotName,
          position: toast.POSITION.BOTTOM_RIGHT,
          exclude: true,
          autoClose: 5000,
        }
      );
      return;
    }
    const tmpDate = new Date(data?.expire_date)?.setHours(23, 59, 59);
    // console.log('dests', dests)
    // return;
    const requestPayload = {
      dest_tanks: dests,
      priority: data?.priority.value,
      // expire_date: addMinutes(
      //   new Date(data?.expire_date)?.setHours(23, 59, 59),
      //   -new Date(data?.expire_date).getTimezoneOffset()
      // ).toISOString(),
      is_exact_date: isExactDate,
      expire_date: new Date(tmpDate)?.toISOString(),
      type: type,
      subtype: data?.subtype?.value,
      winery_id: activeWinery?.id,
      cellarman_ids: Array.isArray(data?.cellarman_ids)
        ? data.cellarman_ids.map((cellMan) => cellMan?.value)
        : [],
      new_batch_name: data?.new_batch_name,
      src_tanks: [
        {
          tank_id: data?.tank_id_in?.value,
          batch_id: data?.tank_id_in?.lot_id,
          old_quantity: data?.tank_id_in?.quantity,
          new_quantity: Number(data?.tank_id_in?.quantity) - getTotalQuantity(),
          isDecantDone: wineMakingData?.dest_tanks?.some?.(
            (tank) => data?.tank_id_in?.value == tank?.tank_id
          ),
        },
      ],
      pomaces: data?.pomaces,
      note: data?.note,
    };

    if (type === "FILTERING") {
      let errorMessage;
      const filterType = filter_type?.value;
      if (filterType === "HOUSING") {
          const filters = data?.housing_filters?.filter(f => f?.filterData?.value)?.map(f => f?.filterData) || [];
          const allFiltersActive = filters.every((f) => f?.status === "ACTIVE");
          const allCandlesActive = filters.every(filter =>
            filter?.candles?.every(candle => candle?.status === "ACTIVE")
          );
          
          if (filters?.length === 0 || data?.housing_filters?.length !== filters?.length) errorMessage = "toast_missingFilters";
          else if (!allFiltersActive) errorMessage = "toast_filtersNotAllActive";
          else if (!allCandlesActive) errorMessage = "toast_filterCandlesNotAllActive";
          else requestPayload.filters = filters;
      } else {
        requestPayload.filters = [data?.filter];
      }

      if (errorMessage) {
        toast.error(getLabel(errorMessage),
          {
            toastId: customIdInvalidFilters,
            position: toast.POSITION.BOTTOM_RIGHT,
            exclude: true,
            autoClose: 5000,
          }
        );
        return;
      }
      
      requestPayload.filter_type = filterType;
    } else if (type === "RACKING") {
      const tmpPomaces = [];
      if (data?.pomaces) {
        tmpPomaces.push(buildPomace(data.pomaces));
      }
      requestPayload.pomaces = tmpPomaces;
    }

    if (onConfigSave) {
      console.log('dest_tanks', requestPayload.dest_tanks)
      onConfigSave({
        src_tanks: requestPayload.src_tanks,
        dest_tanks: requestPayload.dest_tanks,
        operation_params: {
          type: requestPayload.type,
          subtype: requestPayload.subtype,
        },
        pomaces: requestPayload.pomaces || [],
      });
      return;
    }

    if (wineMakingData?.dest_tanks?.length > 0) {
      const wineMakingDecantRequestPayload =
        JSON.parse(localStorage.getItem("wineMakingDecantRequestPayload")) ||
        [];
      wineMakingDecantRequestPayload.push(requestPayload);
      localStorage.setItem(
        "wineMakingDecantRequestPayload",
        JSON.stringify(wineMakingDecantRequestPayload)
      );

      // console.log(wineMakingData);

      localStorage.setItem(
        "wineMakingData",
        JSON.stringify({
          ...wineMakingData,
          dest_tanks: wineMakingData?.dest_tanks?.filter(
            (d) => d?.tank_id !== data?.tank_id_in?.value
          ),
        })
      );

      // handleCloseModal();
      navigate("/programming/decant/new");
      return;
    }
    console.log("requestPayload", requestPayload);
    // return;

    let response = null;
    if (id && !isDuplicate) {
      response = await toastAxios(
        () => updateOperation(id, requestPayload, axiosPrivate),
        customIdUpdateOp,
        { name: operation?.type || "" },
        getLabel,
      );
    } else {
      response = await toastAxios(
        () => newOperation(requestPayload, axiosPrivate),
        customIdNewOp,
        { name: operation?.type || "" },
        getLabel,
      );
      if (response?.requires_confirmation === true) {
        setPendingPayload(requestPayload);
        setConflictProtocols(response.affected_protocols || []);
        setIsConflictOpen(true);
        return;
      }
    }

    if (response && response?.success) {
      const updated_op = response?.updated_data || null;
      // NEW: Pass affected_entities to updateStore for surgical cache updates
      updateStore(updated_op, response?.affected_entities);
      navClickHandler();
    }
  };

  const handleConflictConfirm = async () => {
    setIsConflictOpen(false);
    const response = await toastAxios(
      () => newOperation({ ...pendingPayload, confirm_protocol_impact: true }, axiosPrivate),
      customIdNewOp,
      { name: operation?.type || "" },
      getLabel,
    );
    if (response?.success) {
      updateStore(response.updated_data, response.affected_entities);
      navClickHandler();
    }
  };

  // NEW: Updated to accept affected_entities for surgical cache updates
  const updateStore = async (payloadOp, affectedEntities) => {
    // TO DO update also involved batches and tanks
    if (id && !isDuplicate) {
      dispatch(updateOp(payloadOp));
    } else {
      dispatch(add(payloadOp));
    }

    // NEW: Surgical cache update using affected_entities
    if (affectedEntities) {
      const cacheManager = new CacheManager(dispatch, axiosPrivate);
      await cacheManager.processAffectedEntities(affectedEntities);
    }
    // ⚠️ FALLBACK: If backend doesn't return affected_entities yet
    else {
      dispatch(emptyLots());
      dispatch(emptyTanks());
    }

    // if (payloadTanks?.id) {
    //   dispatch(update(payloadTanks));
    // } else {
    //   dispatch(add(payloadTanks));
    // }

    // if (payloadBatches?.id) {
    //   dispatch(update(payloadBatches));
    // } else {
    //   dispatch(add(payloadBatches));
    // }
  };
  
  const navClickHandler = () => {
    if (id) {
      navigate(`/programming/${type?.toLowerCase?.()}/${id}`);
    } else {
      navigate("/programming");
    }
  };

  const handleCloseModal = () => {
    setIsOpen(false);
  };

  const getTotalQuantity = (isOperation = false) => {
    // const batch_id_in = getTankData(tank_id_in, true) || {};
    
    if (
      !id &&
      !isOperation &&
      tank_id_in?.is_planned &&
      Number(tank_id_in?.planned_quantity) <= 0
    )
      return 0; // i.e. if the in batch is all planned in other dests
    // getTotalQuantity deve restituire LITRI (sottratto a quantità in litri).
    // I valori del form (dest_tanks/pomaces) sono in unità input quando non si
    // legge da operation (già in litri dal BE).
    const pomaces =
      type === "RACKING"
        ? inputToLiters(getValues("pomaces")?.quantity || 0, inputUnit)
        : 0;

    return (pomaces +
      (isOperation ? operation?.dest_tanks : dest_tanks)?.reduce(
        (acc, curr) => {
          const q = isOperation
            ? Number(curr?.quantity) || 0
            : inputToLiters(curr?.quantity, inputUnit);
          return acc + (q || 0);
        },
        0
      ) || 0
    );
  };

  const openFormModal = () => {
    // trigger(); // TO DO remove trigger from all
    const expDate = new Date(getValues("expire_date"));
    if (new Date(expDate?.setHours(23, 59, 59)) < new Date()) {
      setError("expire_date", {
        type: "manual",
        message: getLabel("errorDateInThePast"),
      });
      return;
    } else clearErrors("expire_date");

    if ((type === "CLOSING" && dest_tanks?.filter((t) => String(t?.close_dest?.value))?.length === 0) || (type !== "CLOSING" && dest_tanks?.filter((t) => t?.tankData?.value)?.length === 0)) {
      toast(getLabel("toast_destemmingMissingDests"), {
        toastId: customIdMissingDests,
        type: toast.TYPE.ERROR,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    } else {
      toast.dismiss(customIdMissingDests);
    }

    if (type !== "CLOSING" && 
      dest_tanks?.filter(
        (t) =>
          (!t?.batch_name_selector?.value &&
            t?.batch_name_selector?.label?.length === 0) ||
          (t?.batch_name_selector?.value === true &&
            t?.new_batch_name?.length === 0)
      )?.length > 0
    ) {
      toast(getLabel("toast_destemmingMissingLotSelectors"), {
        toastId: customIdMissingDests,
        type: toast.TYPE.ERROR,
        isLoading: false,
        position: toast.POSITION.BOTTOM_RIGHT,
      });
      return;
    } else {
      toast.dismiss(customIdMissingDests);
    }
    const transferLiters = getTotalQuantity();
    
    const totalLitersAvailable = Number(tank_id_in?.planned_quantity);
    if (transferLiters <= totalLitersAvailable) {
      toast.dismiss(customIdInvalidQuantities);
      setIsOpen(true);
    } else {
      toast(
        getLabel("toast_transferInvalidQuantities", {
          sum: transferLiters || "0",
          total: totalLitersAvailable,
        }),
        {
          toastId: customIdInvalidQuantities,
          type: toast.TYPE.ERROR,
          isLoading: false,
          position: toast.POSITION.BOTTOM_RIGHT,
        }
      );
      return;
    }
  };

  const customIdMissingDests = "custom-id-missing-dests";
  const customIdInvalidFilters = "custom-id-invalid-filters";
  const customIdInvalidLotName = "custom-id-invalid-lot-name";
  const customIdInvalidQuantities = "custom-id-invalid-quantities";

  useEffect(() => {
    if (!dest_tanks || dest_tanks?.length === 0) return;

    setIsPressed((prevPressed) => {
      return dest_tanks?.map((tank, idx) => {
        const dest_name = tank?.batch_name_selector?.value
          ? tank?.new_batch_name
          : tank?.batch_name_selector?.label;
  
        const existingBatch = lots?.find(
          (l) => l?.name === dest_name && l?.state === 'WINE'
        );
        // const existingBatch = dest_name ? (lots?.find(l => l?.name === dest_name && l?.status === 'CONFIRMED' && l?.state === 'WINE') !== undefined) || false : false; //l?.tanks?.some(ll => ll?.tank_id == dest_tanks?.[idx]?.tankData?.value));
        const newLotIndex = dest_tanks?.findIndex(d => d?.new_batch_name === tank?.batch_name_selector?.label);
        if (!existingBatch && tank?.batch_name_selector?.value === false && newLotIndex > -1) {
          return prevPressed?.[newLotIndex];
        }

        if (tank?.tankData?.quantity === 0 && !tank?.tankData?.is_planned && !existingBatch) return prevPressed?.[idx];
    
        return existingBatch?.batch_type === 0;
      });
    });
  }, [dest_tanks, lots]);
  
  const getDestTanksForRecap = () => {
    const isClose = type === "CLOSING";
    const isRacking = type === "RACKING";
    const isFiltering = type === "FILTERING";
    const dests = dest_tanks?.map((d, index) => {
      const new_batch_name = 
        d?.batch_name_selector?.value === true
          ? d?.new_batch_name
          : d?.batch_name_selector?.label;
          
      let color = d?.tankData?.color || tank_id_in?.color;
      const subtypeValue = subtype?.value;
      if (subtypeValue) {
        if (subtypeValue === "ORANGE_WINE" && color === "WHITE") color = "ORANGE";
        else if (subtypeValue === "SALASSO" && color === "RED") color = "ROSE";
      }
    
      // il recap mostra le quantità via formatTankUnit (litri): convertiamo
      const addQuantityLiters = inputToLiters(d?.quantity, inputUnit);
      return {
        ...d?.tankData,
        name: d?.tankData?.label,
        color: color,   // d?.tankData?.color || tank_id_in?.color,
        quantity: (addQuantityLiters || 0) + (Number(d?.tankData?.quantity) || 0),
        add_quantity: addQuantityLiters,
        state: isRacking ? 'WINE' : tank_id_in?.state,
        batch_name: new_batch_name,
        ...(isRacking && {
          batch_type: lotTypeMap?.[new_batch_name] || 0,    // default to PRESSED
        }),
        ...(isFiltering && {
          batch_type: tank_id_in?.batch_type || 0,    // default to PRESSED
        }),
        ...(isClose && {
          close_dest: d?.close_dest?.label,
          close_reason: d?.close_reason?.value,
          // close_notes: d?.close_notes,
          type: "close",
        }),
      };
    }) || [];
    
    if (isRacking) {
      const pomaces = getValues('pomaces');
      const pomaces_name = pomaces?.dest?.label || '';
      const pomaces_quantity = inputToLiters(pomaces?.quantity || 0, inputUnit);
      if (pomaces_quantity <= 0 || !pomaces_name || pomaces_name?.length === 0) return dests;
      // add pomaces as a dest tank with quantity and name only
      dests.push({
        type: 'pomaces',
        waste_type: pomaces?.reason?.value || null,
        name: pomaces_name,
        color: tank_id_in?.color,
        quantity: pomaces_quantity,
        add_quantity: pomaces_quantity,
        state: isRacking ? 'WINE' : tank_id_in?.state,
        batch_name: pomaces?.dest,
      });
    }
    
    return dests;
  }

  const isFilterAlreadyUsedInForm = (filter) => {
    return !housing_filters?.some?.(
      (field) =>
        field?.filterData?.value == filter?.id
    );
  };

  const getFiltersByType = () => {
    const filterType = filter_type?.value;
    
    if (!["CARTON", "HOUSING", "TANGENTIAL"].includes(filterType)) return [];
    const tmpFilters = (
      filterType === "HOUSING" ? 
        filters?.filter((f) => isFilterAlreadyUsedInForm(f))
        ?.map(f => { return { ...f, is_disabled: f?.candles?.length === 0}}) 
        : filters
      ) || [];

    return tmpFilters?.filter(f => f?.type === filterType)?.map(f => { return { ...f, value: f?.id, label: `${f?.filter_name} ${f?.serial_number ? ('(' + f?.serial_number + ')') : ''}`}}) || [];
  }

  const isFilterPlusButtonDisabled = () => {
    const len = housing_filters?.length;
    const lastElement = housing_filters?.[len - 1];
    const tmpFilters = filter_type?.value === "HOUSING" ? filters?.map(f => { return { ...f, is_disabled: f?.candles?.length === 0}}) : filters;
    const allFiltersByTypeLen = tmpFilters?.filter(f => f?.type === filter_type?.value && f?.is_disabled !== true)?.length || 0;
    
    return !lastElement?.filterData?.id || (len >= allFiltersByTypeLen);
  };

  const getFiltersData = () => {
    const filterType = filter_type?.value;
    if (type !== "FILTERING" || !filterType) return [];
    const filter = getValues("filter") || null;
    if (filter && (filterType === "CARTON" || filterType === "TANGENTIAL")) return [filter];
    else {
      const filters = getValues("housing_filters") || [];
      return filters?.map(f => f?.filterData) || [];
    }
  }

  if (type === "FILTERING" && tanks?.filter(t => t?.state === "WINE" && t?.quantity > 0)?.length === 0) {
    return (<h5 className="alert_no_info">{getLabel("noWineLotsAlert")}</h5>)
  }

  return (
    <div className="operation_form_wrapper">
      <form
        onSubmit={handleSubmit(openFormModal, (errors) => console.log("Form validation errors:", errors))}
        autoComplete="off"
        noValidate
      >
        {isWineMakingApplication && (
          <div className="operation_form_wrapper_row">
            <TankIcon
              tank={tank_id_in}
              isText={true}
              isDestemming={false}
              isSrc={true}
              isRecap={false}
            />
          </div>
        )}
        {!isWineMakingApplication && (
          <SourceSelector
            id={id}
            type={type}
            configs={configs}
            subtype={subtype?.value}
            operation={operation}
            control={control}
            errors={errors}
            tanks={tanks}
            lots={lots}
            tank_id_in={tank_id_in}
            lotTypeMap={lotTypeMap}
            dest_tanks={dest_tanks}
            pomaces_quantity={pomaces?.quantity}
            // lockedSubtype={lockedSubtype}
            isWizardMode={!!onConfigSave}
            getValues={getValues}
            setValue={setValue}
            // getTankData={getTankData}
            isMobile={isMobile}
          />
        )}
        {/* {false && !isWineMakingApplication && forcedSrcTank && tank_id_in?.value && (
          <div className="operation_form_wrapper_row">
            <TankIcon
              tank={tank_id_in}
              isText={true}
              isDestemming={false}
              isSrc={true}
              isRecap={false}
            />
          </div>
        )} */}
        
        <DestinationSelector
          id={id}
          type={type}
          subtype={subtype?.value}
          operation={operation}
          register={register}
          control={control}
          errors={errors}
          clearErrors={clearErrors}
          setError={setError}
          trigger={trigger}
          setValue={setValue}
          getValues={getValues}
          tanks={tanks}
          lots={lots}
          tank_id_in={tank_id_in}
          fields={fields}
          lotNames={lotNames}
          append={append}
          update={update}
          remove={remove}
          // getTankData={getTankData}
          lotTypeMap={lotTypeMap}
          setLotTypeMap={setLotTypeMap}
          isMobile={isMobile}
          watchedArray={dest_tanks}
          axiosPrivate={axiosPrivate}
          isWizardMode={!!onConfigSave}
        />

        {type === "RACKING" && 
          <PomacesDestination
            type={type}
            operation={operation}
            dest_tanks={dest_tanks}
            tank_id_in={tank_id_in}
            register={register}
            control={control}
            errors={errors}
            getLabel={getLabel}
            getValues={getValues}
            setValue={setValue}
            watch={watch}
            clearErrors={clearErrors}
            pomacesRecipients={pomacesRecipients}
            setPomacesRecipients={setPomacesRecipients}
          />}

        {type === "FILTERING" && (
          <>
            <h5>{getLabel("operationFiltering")}</h5>
            <div className="operation_form_wrapper_row">
              <Select
                name="filter_type"
                control={control}
                label={getLabel("filteringType")}
                placeholder={getLabel("stabilizationTypePlaceholder")}
                error={errors}
                required={getLabel("inputRequiredError")}
                // isDisabled={!tank_id_in?.value || !dest_tanks?.[0]?.tankData?.value}
                options={[
                    {
                      value: "CARTON",
                      label: getLabel(
                        "filterType_CARTONOne"
                      ),
                    },
                    {
                      value: "HOUSING",
                      label: getLabel(
                        "filterType_HOUSINGOne"
                      ),
                    },
                    {
                      value: "TANGENTIAL",
                      label: getLabel(
                        "filterType_TANGENTIALOne"
                      ),
                    },
                  ]
                }
                // tooltip={getLabel("substanceStatesTooltip")}
                // onTableChange={(value) => setOldAnalysisSelects((prev) => {return {...prev, state: value?.value}})}
              />
             {["CARTON", "TANGENTIAL"].includes(filter_type?.value) && <Select
                name="filter"
                control={control}
                label={getLabel("filteringSelect")}
                placeholder={getLabel("filteringSelectPlaceholder")}
                error={errors}
                required={getLabel("inputRequiredError")}
                // isDisabled={!filter_type?.value}
                options={getFiltersByType()}
                // tooltip={getLabel("substanceStatesTooltip")}
                // onTableChange={(value) => setOldAnalysisSelects((prev) => {return {...prev, state: value?.value}})}
              />}
              {["CARTON", "TANGENTIAL"].includes(filter_type?.value) && <FilterIcon filter={getValues("filter")} />}
            </div>

            {filter_type?.value === "HOUSING" && <div className="operation_form_wrapper_row vertical">
              {housingFields?.map?.((field, idx) => {
                return (
                  <div key={`housing_filter_${field.id}`} className="operation_form_wrapper_row ">
                    <Select
                      name={`housing_filters.${idx}.filterData`}
                      control={control}
                      label={getLabel("filteringSelect")}
                      placeholder={getLabel("filteringSelectPlaceholder")}
                      error={errors}
                      required={getLabel("inputRequiredError")}
                      // isDisabled={!filter_type?.value}
                      options={getFiltersByType()}
                      // tooltip={getLabel("substanceStatesTooltip")}
                      // onTableChange={(value) => setOldAnalysisSelects((prev) => {return {...prev, state: value?.value}})}
                    />
                    <FilterIcon filter={housing_filters?.[idx]?.filterData} />
                    {housingFields?.length > 1 && (
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          housingRemove(idx);
                        }}
                      >
                        -
                      </button>
                    )}
                  </div>)
              })
              
            }
            <button
              id="filtersD"
              disabled={isFilterPlusButtonDisabled()} // disable if previous is not set
              onClick={(e) => {
                e.preventDefault();
                housingAppend({
                  value: null, label: null, name: null
                });
              }}
            >
              +
            </button>
          </div>}
        </>)}
        
        <h6>{getLabel("operationAdditional")}</h6>
        {!isWineMakingApplication && !onConfigSave && (
          <div className="operation_form_wrapper_row">
            <MultipleSelect
              name={`cellarman_ids`}
              control={control}
              label={getLabel("operationUser")}
              placeholder={getLabel("operationUserPlaceholder")}
              error={errors["cellarman_ids"]}
              required={getLabel("inputRequiredError")}
              options={users?.map?.((user) => {
                return {
                  value: user?.id,
                  label: user?.username,
                };
              })}
              tooltip={getLabel("destemmingCellarmenTooltip")}
            />
            <Select
              name="priority"
              control={control}
              label={getLabel("operationPriority")}
              placeholder={getLabel("operationPriorityPlaceholder")}
              error={errors}
              required={getLabel("inputRequiredError")}
              options={[
                {
                  label: getLabel("lotQuality_HIGH"),
                  value: "2",
                },
                {
                  label: getLabel("lotQuality_MEDIUM"),
                  value: "1",
                },
                {
                  label: getLabel("lotQuality_LOW"),
                  value: "0",
                },
              ]}
              // defaultValue={{
              //     value: "",
              //     label: "",
              //   }}
            />
            <DatePicker
              name="expire_date"
              control={control}
              label={getLabel("operationExpireDateForm")}
              error={errors["expire_date"]}
              required={getLabel("inputRequiredError")}
              dateFormat={configs.shortDateFormat}
              placeholder={getLabel("expiringDatePlaceholder")}
            />
            {getValues("expire_date") && <Input
              name={"expire_date_exact"}
              register={register}
              label={getLabel("isExactDate")}
              error={errors["expire_date_exact"]}
              // isLabel={false}
              type={"checkbox"}
              checked={isExactDate}
              onChange={() => setIsExactDate((prev) => !prev)}
              customClassName={"confirm no_margin_top"}
              tooltip={getLabel("destemmingExactDate")}
            />}
          </div>
        )}
        <div className="operation_form_wrapper_row">
          <TextArea
            name="note"
            register={register}
            label={getLabel("clientNotes")}
            placeholder={getLabel("clientNotesPlaceholder")}
            error={errors["note"]}
          />
        </div>
        {!isWineMakingApplication && !onConfigSave && (
          <div className="operation_form_wrapper_row">
            <Submit label={getLabel("submitForm")}></Submit>
          </div>
        )}
      </form>
      <ConfirmationModal
        isOpen={isOpen}
        onConfirm={sendData}
        onClose={handleCloseModal}
        description={getLabel(
          type === "CLOSING"
            ? "modalNewCloseDescription"
            : type === "FILTERING"
            ? "modalNewFilteringDescription"
            : type === "DECANT"
              ? "modalNewDecantDescription"
              : "modalNewRackingDescription",
          {
            name: operation?.id || "",
          }
        )}
        isOperationRecap={true}
        totalStep={0}
        errors={errors}
        getValues={getValues}
        // setNewBatchName={setNewBatchName}
      >
        <OperationRecap
          type={type}
          control={control}
          register={register}
          getValues={getValues}
          errors={errors}
          lots={lots}
          lotNames={lots?.map((l) => l.name)}
          dest_tanks={getDestTanksForRecap(dest_tanks, lots, getValues, isPressed)}
          src_tanks={[{ ...tank_id_in,
            is_src: true,
            name: tank_id_in?.label,
            batch_name: tank_id_in?.lot,
            quantity: (tank_id_in?.is_planned_src ? (tank_id_in?.planned_quantity - (tank_id_in?.planned_pomaces || 0)) : tank_id_in?.quantity) - getTotalQuantity(),
            arrow_quantity: getTotalQuantity(),
            is_planned: tank_id_in?.is_planned_src,
          }]}
          additionalData={getFiltersData()}
          // src_tanks={[getTankData(tank_id_in, true)]}
          // newBatchName={newBatchName}
        />
      </ConfirmationModal>
      <ProtocolConflictModal
        isOpen={isConflictOpen}
        protocols={conflictProtocols}
        onConfirm={handleConflictConfirm}
        onCancel={() => setIsConflictOpen(false)}
      />
    </div>
  );
});

TransferForm.displayName = "TransferForm";

export default TransferForm;
